import React, { useState } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";
import type { ImpactAssessmentRow, ImpactSubmission } from "./types";

export interface ShowAllEntry {
  label: string;
  analysis: string;
  summary: string;
  recommendation: string;
  hasImpact: boolean;
  impactLabel: string;
  impactBg: string;
  impactColor: string;
}

function buildEntries(row: ImpactAssessmentRow): ShowAllEntry[] {
  const system: ShowAllEntry = {
    label: row.triggeredBy || "System",
    analysis: row.analysis,
    summary: row.summary,
    recommendation: row.recommendation,
    hasImpact: row.hasImpact,
    impactLabel: row.impactLabel,
    impactBg: row.impactBg,
    impactColor: row.impactColor,
  };
  const submissions = row.submissions.map((sub: ImpactSubmission) => ({
    label: sub.name,
    analysis: sub.analysis,
    summary: sub.summary,
    recommendation: sub.recommendation,
    hasImpact: sub.hasImpact,
    impactLabel: sub.impactLabel,
    impactBg: sub.impactBg,
    impactColor: sub.impactColor,
  }));
  return [system, ...submissions];
}

export interface ShowAllImpactModalProps {
  open: boolean;
  row: ImpactAssessmentRow | null;
  onClose: () => void;
}

export const ShowAllImpactModal: React.FC<ShowAllImpactModalProps> = ({
  open,
  row,
  onClose,
}) => {
  const [activeIdx, setActiveIdx] = useState(0);

  React.useEffect(() => {
    if (open) setActiveIdx(0);
  }, [open, row?.id]);

  const entries = row ? buildEntries(row) : [];
  const active = entries[activeIdx] || entries[0];
  const title =
    row?.dimension && row?.tagValue
      ? `${row.dimension}: ${row.tagValue}`
      : "";

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(onClose)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          maxWidth: 720,
          maxHeight: "86vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 15,
          fontWeight: 700,
          color: "#101828",
          pt: 2.5,
          px: 3,
          pb: 0,
        }}
      >
        {title}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: "#99A1AF" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          px: 3,
          pt: 1.75,
          borderBottom: "1px solid #F3F4F6",
        }}
      >
        {entries.map((entry, idx) => {
          const isActive = idx === activeIdx;
          return (
            <Box
              key={`${entry.label}-${idx}`}
              component="button"
              type="button"
              onClick={() => setActiveIdx(idx)}
              sx={{
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                pb: 1.5,
                px: 0,
                color: isActive ? "#1447E6" : "#6A7282",
                borderBottom: "2px solid",
                borderColor: isActive ? "#1447E6" : "transparent",
              }}
            >
              {entry.label}
            </Box>
          );
        })}
      </Box>

      <DialogContent
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {active?.hasImpact && active.impactLabel ? (
          <Box>
            <Box
              component="span"
              sx={{
                bgcolor: active.impactBg,
                color: active.impactColor,
                borderRadius: "6px",
                px: 1.25,
                py: 0.35,
                fontSize: 11,
              }}
            >
              {active.impactLabel}
            </Box>
          </Box>
        ) : null}

        {(
          [
            ["Analysis", active?.analysis],
            ["Summary", active?.summary],
            ["Recommendation", active?.recommendation],
          ] as const
        ).map(([label, text]) => (
          <Box key={label}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "#6A7282",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                mb: 1,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: "#101828",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
              }}
            >
              {text || "–"}
            </Typography>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};
